import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import HeroSection from './HeroSection';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
const HomePage = ({ darkMode, posts, portfolioItems, setActivePage, formatDate, isAdmin, setSelectedPost }) => {
  /**
   * Returns an excerpt of the given content, limited to `limit` characters.
   */
  const getExcerpt = (htmlContent, limit = 150) => {
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Return a truncated string plus "..."
    return textContent.length > limit
      ? textContent.substring(0, limit) + '...'
      : textContent;
  };

  // Function to determine the image source based on what's available
  const getImageSrc = (item) => {
    // If an uploaded image file is available, use its object URL
    if (item.imageFile && typeof item.imageFile === 'object') {
      return URL.createObjectURL(item.imageFile);
    }
    // Otherwise, use the provided imageUrl
    return item.imageUrl || '/api/placeholder/600/400';
  };

  const profileData = {
    name: "berkeakturk1",
    username: "berkeakturk1",
    email: "aktrk-berke@yandex.com",
    profileImage: "/imgs/pfp.jpg",
    techStack: [
      { name: "JavaScript", icon: "js", color: "#F7DF1E", textColor: "#000000" },
      { name: "TypeScript", icon: "ts", color: "#3178C6", textColor: "#FFFFFF" },
      { name: "C++", icon: "cpp", color: "#00599C", textColor: "#FFFFFF" },
      { name: "C#", icon: "csharp", color: "#239120", textColor: "#FFFFFF" }
    ],
    links: [
      { name: "GitHub", url: "https://github.com/berkeakturk1", icon: "github" },
      { name: "LinkedIn", url: "https://linkedin.com/in/berkeakturk", icon: "linkedin" }
    ]
  };

  // Theme classes for consistent styling
  const themeClasses = {
    background: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    sectionHeading: `text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} tracking-tight`,
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-300' : 'text-gray-600',
    viewAllBtn: darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700',
    ctaBackground: darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100',
    ctaButton: darkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white',
    shadow: 'shadow-lg hover:shadow-xl transition-all duration-300'
  };

  return (
    <div className={`${themeClasses.background} min-h-screen`}>
      <div className="home-page space-y-24 max-w-7xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <HeroSection 
          darkMode={darkMode}
          profileImage={profileData.profileImage}
          isAdmin={isAdmin}
        />

        {/* Blog Section */}
        <section className="blogs fade-in-up">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <h2 className={`${themeClasses.sectionHeading} relative`}>
              <span className="relative inline-block pb-3">
                Recent Articles
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
              </span>
            </h2>
            <button
              onClick={() => setActivePage('blog')}
              className={`mt-2 sm:mt-0 group flex items-center text-sm px-5 py-2.5 rounded-xl transition-all duration-300 hover:scale-105 font-medium shadow-md hover:shadow-lg ${
                darkMode ? 'bg-gray-800/70 text-indigo-400 hover:bg-gray-700' : 'bg-white text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              View All
              <svg className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
          </div>
          
          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.slice(0, 4).map((post, index) => (
                <div
                  key={post.id}
                  className={`rounded-2xl overflow-hidden ${themeClasses.card} hover-lift shadow-xl hover:shadow-2xl transition-all duration-300 border ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'} fade-in-up`}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${getCategoryGradient(post.category, darkMode)}`}></div>
                  <div className="p-7">
                    <div className="flex justify-between items-start mb-5">
                      <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${getCategoryBadgeColor(post.category, darkMode)} shadow-sm`}>
                        {post.category}
                      </span>
                      <span className={`text-xs font-medium ${themeClasses.subtext}`}>{formatDate(post.date)}</span>
                    </div>

                    <h3 className={`text-xl md:text-2xl font-bold mb-4 line-clamp-2 ${themeClasses.text} hover:text-gradient transition-all duration-300`}>{post.title}</h3>
                    
                    <div className={`mt-3 text-sm leading-relaxed ${themeClasses.subtext}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {getExcerpt(post.content, 150)}
                      </ReactMarkdown>
                    </div>
                    
                    <button
                      onClick={() => {
                        setSelectedPost(post);
                        setActivePage('blog-details');
                      }}
                      className={`mt-6 inline-flex items-center px-4 py-2 rounded-lg font-medium group transition-all duration-300 hover:scale-105 ${
                        darkMode ? 'bg-gradient-to-r from-indigo-600/20 to-purple-600/20 hover:from-indigo-600/30 hover:to-purple-600/30 text-indigo-400' : 'bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 text-indigo-600'
                      }`}
                    >
                      Continue Reading
                      <svg className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`p-10 text-center rounded-xl ${themeClasses.card} ${themeClasses.shadow}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto mb-6 text-indigo-400 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <p className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>No blog posts available yet.</p>
              <p className={`text-lg ${themeClasses.subtext}`}>Check back soon for new content!</p>
            </div>
          )}
        </section>

        {/* Portfolio Section */}
        <section className="portfolio fade-in-up" style={{animationDelay: '0.2s'}}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <h2 className={`${themeClasses.sectionHeading} relative`}>
              <span className="relative inline-block pb-3">
                Featured Projects
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
              </span>
            </h2>
            <button
              onClick={() => setActivePage('portfolio')}
              className={`mt-2 sm:mt-0 group flex items-center text-sm px-5 py-2.5 rounded-xl transition-all duration-300 hover:scale-105 font-medium shadow-md hover:shadow-lg ${
                darkMode ? 'bg-gray-800/70 text-indigo-400 hover:bg-gray-700' : 'bg-white text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              View All
              <svg className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
          </div>

          {portfolioItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {portfolioItems.slice(0, 3).map((item, index) => (
                <div
                  key={item.id}
                  className={`group rounded-2xl overflow-hidden ${themeClasses.card} hover-lift shadow-xl hover:shadow-2xl transition-all duration-300 border ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'} fade-in-up`}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="relative overflow-hidden aspect-video bg-gradient-to-br from-indigo-100 to-purple-100">
                    <img
                      src={getImageSrc(item)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1"
                      onError={(e) => {
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = '/api/placeholder/600/400'; // Fallback image
                      }}
                    />
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-95 transition-all duration-300 ${
                      darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900' : 'bg-gradient-to-br from-white via-indigo-50/50 to-white'
                    } flex items-center justify-center backdrop-blur-sm`}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-110 shadow-lg ${
                          darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-purple-500/50' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-indigo-400/50'
                        }`}
                      >
                        View Project
                      </a>
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className={`text-xl font-bold mb-4 ${themeClasses.text} group-hover:text-gradient transition-all duration-300`}>{item.title}</h3>
                    <p className={`text-sm mb-5 line-clamp-2 leading-relaxed ${themeClasses.subtext}`}>
                      {item.description.length > 100
                        ? item.description.substring(0, 100) + '...'
                        : item.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags && (Array.isArray(item.tags) ? item.tags : item.tags.split(',').map(tag => tag.trim())).slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className={`text-xs px-3 py-1.5 rounded-full font-semibold shadow-sm transition-all duration-300 hover:scale-105 ${
                            darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 text-gray-200' : 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      {item.tags && (Array.isArray(item.tags) ? item.tags.length > 3 : item.tags.split(',').length > 3) && (
                        <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          +{(Array.isArray(item.tags) ? item.tags.length : item.tags.split(',').length) - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`p-10 text-center rounded-xl ${themeClasses.card} ${themeClasses.shadow}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto mb-6 text-indigo-400 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <p className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>No portfolio items available yet.</p>
              <p className={`text-lg ${themeClasses.subtext}`}>Check back soon to see my latest projects!</p>
            </div>
          )}
        </section>
        
        {/* Contact CTA Section */}
        <section className={`cta-section text-center py-16 px-8 rounded-3xl mb-12 relative overflow-hidden fade-in-up border ${darkMode ? 'border-gray-700/50' : 'border-indigo-100'}`} style={{animationDelay: '0.4s'}}>
          {/* Animated background blobs */}
          <div className="absolute inset-0 opacity-20">
            <div className={`absolute top-0 right-0 w-64 h-64 rounded-full filter blur-3xl ${darkMode ? 'bg-purple-600' : 'bg-indigo-300'} animate-pulse`}></div>
            <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-full filter blur-3xl ${darkMode ? 'bg-indigo-600' : 'bg-purple-300'} animate-pulse`} style={{animationDelay: '1.5s'}}></div>
          </div>

          <div className="max-w-3xl mx-auto relative z-10">
            <h2 className={`text-3xl md:text-4xl font-extrabold mb-6 ${themeClasses.text}`}>Ready to Collaborate?</h2>
            <p className={`text-lg max-w-2xl mx-auto mb-10 leading-relaxed ${themeClasses.subtext}`}>
              Let's discuss how I can help bring your ideas to life with creative solutions tailored to your needs.
            </p>
            <a
  href="mailto:contact@berkeakturk.com"
  className={`px-10 py-4 rounded-xl font-semibold transition-all transform hover:scale-110 shadow-2xl inline-flex items-center hover-lift ${
    darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-purple-500/40' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-indigo-400/50'
  }`}
>
  <svg className="mr-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
  </svg>
  Get in Touch
</a>


            <div className="flex justify-center space-x-5 mt-8">
  {profileData.links.map((link, index) => {
    // Determine the correct icon based on the link.icon property.
    let IconComponent;
    switch (link.icon.toLowerCase()) {
      case "github":
        IconComponent = FaGithub;
        break;
      case "linkedin":
        IconComponent = FaLinkedin;
        break;
      default:
        IconComponent = null;
    }
    return (
      <a
        key={index}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`p-3 rounded-full transition-all duration-300 hover:scale-125 shadow-lg ${darkMode ? 'bg-gray-800/70 hover:bg-gray-700 text-gray-300 hover:text-white' : 'bg-white hover:bg-indigo-100 text-indigo-600 hover:text-indigo-700'}`}
        aria-label={link.name}
      >
        {IconComponent ? <IconComponent className="text-xl" /> : <span className="text-xl">{link.name[0]}</span>}
      </a>
    );
  })}
</div>
          </div>
        </section>
      </div>
    </div>
  );
};

// Helper functions for category colors
const getCategoryColor = (category, darkMode) => {
  const categories = {
    'Technology': darkMode ? 'bg-indigo-600' : 'bg-indigo-500',
    'Design': darkMode ? 'bg-purple-600' : 'bg-purple-500',
    'Business': darkMode ? 'bg-emerald-600' : 'bg-emerald-500',
    'Personal': darkMode ? 'bg-amber-600' : 'bg-amber-500',
    'Other': darkMode ? 'bg-gray-600' : 'bg-gray-500'
  };

  return categories[category] || categories['Other'];
};

const getCategoryGradient = (category, darkMode) => {
  const categories = {
    'Technology': 'from-indigo-500 via-blue-500 to-purple-500',
    'Design': 'from-purple-500 via-pink-500 to-red-500',
    'Business': 'from-emerald-500 via-teal-500 to-cyan-500',
    'Personal': 'from-amber-500 via-orange-500 to-yellow-500',
    'Other': 'from-gray-500 via-gray-600 to-gray-700'
  };

  return categories[category] || categories['Other'];
};

const getCategoryBadgeColor = (category, darkMode) => {
  const categories = {
    'Technology': darkMode ? 'bg-indigo-900/70 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    'Design': darkMode ? 'bg-purple-900/70 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-200',
    'Business': darkMode ? 'bg-emerald-900/70 text-emerald-300 border border-emerald-700' : 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    'Personal': darkMode ? 'bg-amber-900/70 text-amber-300 border border-amber-700' : 'bg-amber-100 text-amber-800 border border-amber-200',
    'Other': darkMode ? 'bg-gray-700/70 text-gray-300 border border-gray-600' : 'bg-gray-200 text-gray-800 border border-gray-300'
  };

  return categories[category] || categories['Other'];
};

export default HomePage;